import { useExportForumCompanyPackagesMutation } from "api/queries/forumCompanies";
import type { ExportForumCompanyPackagesRequest } from "api-client/controllers/forumCompanies";
import DropdownButton from "components/DropdownButton";
import { Modal } from "components/styleguide";
import { FileExportType } from "generated/api";
import React, { FC } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import { downloadFileByURL } from "utils/fileUtils";

interface ExportSupplierPackageButtonProps {
  exportParams: Omit<ExportForumCompanyPackagesRequest, "exportType" | "selectedAttendeeIds">;
  isFiltered: boolean;
  isMatchingAttendeesCountLoading?: boolean;
  matchingAttendeesCount?: number;
  selectedAttendeeIds?: number[];
}

const ExportSupplierPackageButton: FC<ExportSupplierPackageButtonProps> = ({
  exportParams,
  isFiltered,
  isMatchingAttendeesCountLoading = false,
  matchingAttendeesCount = 0,
  selectedAttendeeIds = [],
}) => {
  const { t } = useTranslation();
  const hasSelectedAttendees = selectedAttendeeIds.length > 0;

  const exportPackagesMutation = useExportForumCompanyPackagesMutation({
    onSuccess: ({ data }) => downloadFileByURL(data),
    onError: (error: any) => {
      if (error?.response?.status === 404) {
        toast.error(t("No matching supplier package records were found to export"));
      } else {
        toast.error(t("Supplier Package Export failed. Please try again"));
      }
    },
  });

  const exportPackages = (exportType: FileExportType) => {
    exportPackagesMutation.mutate({
      ...exportParams,
      exportType,
      selectedAttendeeIds: hasSelectedAttendees ? selectedAttendeeIds : undefined,
    });
  };

  const requestExport = (exportType: FileExportType) => {
    if (hasSelectedAttendees) {
      Modal.confirm({
        title: t("Selected rows export"),
        content: t(
          "The Supplier Package Export will include only selected rows ({{count}} attendee records). Continue?",
          { count: selectedAttendeeIds.length },
        ),
        okText: t("Export"),
        cancelText: t("Cancel"),
        closable: true,
        onOk: () => exportPackages(exportType),
      });
      return;
    }

    if (isFiltered) {
      if (matchingAttendeesCount === 0) {
        toast.warn(t("No attendees match the applied filters"));
        return;
      }

      Modal.confirm({
        title: t("Filtered dataset export"),
        content: t(
          "The Supplier Package Export will only reference companies associated with the filtered dataset ({{count}} attendee records)",
          { count: matchingAttendeesCount },
        ),
        okText: t("Export"),
        cancelText: t("Cancel"),
        closable: true,
        onOk: () => exportPackages(exportType),
      });
      return;
    }

    exportPackages(exportType);
  };

  const items = [
    {
      label: t("Excel"),
      key: "excel",
      onClick: () => requestExport(FileExportType.Xls),
    },
    {
      label: t("CSV"),
      key: "csv",
      onClick: () => requestExport(FileExportType.Csv),
    },
  ];

  return (
    <DropdownButton
      title="Supplier Package Export"
      items={items}
      isLoading={
        exportPackagesMutation.isLoading || (isFiltered && !hasSelectedAttendees && isMatchingAttendeesCountLoading)
      }
    />
  );
};

export default ExportSupplierPackageButton;
